import { useCallback, useEffect, useState } from 'react';
import * as adminApi from '../../api/adminApi';
import { useAuth } from '../../auth/AuthProvider';

const CONNECTION_TYPES = [
  'ORIGINATOR_TO_PROVIDER',
  'PROVIDER_TO_CUSTODIAN',
  'ORIGINATOR_TO_CUSTODIAN',
];

export function AdminConnectionsPage() {
  const { session } = useAuth();
  const [connections, setConnections] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    from_org_id: '',
    to_org_id: '',
    connection_type: 'ORIGINATOR_TO_CUSTODIAN',
  });

  const load = useCallback(async () => {
    setError(null);
    try {
      const [connData, orgData] = await Promise.all([
        adminApi.listConnections(),
        adminApi.listRegistryOrganizations({ status: 'ACTIVE', page_size: 100 }),
      ]);
      setConnections(connData || []);
      setOrgs(orgData.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load connections');
      setConnections([]);
    }
  }, []);

  useEffect(() => {
    if (session?.isPlatformAdmin) {
      load();
    }
  }, [session?.isPlatformAdmin, load]);

  if (!session?.isPlatformAdmin) {
    return (
      <p className="text-sm text-eorig-muted">
        Platform admin access is required for registry connections.
      </p>
    );
  }

  async function handleCreate(event) {
    event.preventDefault();
    setError(null);
    try {
      await adminApi.createConnection(form);
      setForm({
        from_org_id: '',
        to_org_id: '',
        connection_type: 'ORIGINATOR_TO_CUSTODIAN',
      });
      await load();
    } catch (err) {
      setError(err.message || 'Create failed');
    }
  }

  async function handleDelete(id) {
    setError(null);
    try {
      await adminApi.deleteConnection(id);
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  }

  return (
    <div className="space-y-6">
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      <form
        onSubmit={handleCreate}
        className="grid gap-3 rounded-2xl border border-eorig-border bg-white p-4 sm:grid-cols-2"
      >
        <h2 className="sm:col-span-2 text-sm font-semibold">Link organizations</h2>
        <select
          required
          value={form.from_org_id}
          onChange={(e) => setForm((f) => ({ ...f, from_org_id: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        >
          <option value="">From organization</option>
          {orgs.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name} ({org.org_type})
            </option>
          ))}
        </select>
        <select
          required
          value={form.to_org_id}
          onChange={(e) => setForm((f) => ({ ...f, to_org_id: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm"
        >
          <option value="">To organization</option>
          {orgs.map((org) => (
            <option key={org.id} value={org.id}>
              {org.name} ({org.org_type})
            </option>
          ))}
        </select>
        <select
          value={form.connection_type}
          onChange={(e) => setForm((f) => ({ ...f, connection_type: e.target.value }))}
          className="rounded-xl border border-eorig-border px-3 py-2 text-sm sm:col-span-2"
        >
          {CONNECTION_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="rounded-xl bg-umb-navy px-4 py-2 text-sm font-semibold text-white sm:col-span-2"
        >
          Create connection
        </button>
      </form>

      <div className="overflow-hidden rounded-2xl border border-eorig-border bg-white">
        {connections.length === 0 ? (
          <p className="p-4 text-sm text-eorig-muted">No connections yet.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-eorig-border bg-eorig-surface text-xs uppercase text-eorig-muted">
              <tr>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">To</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {connections.map((conn) => (
                <tr key={conn.id} className="border-b border-eorig-border/70">
                  <td className="px-4 py-3">{conn.from_org_name || conn.from_org_id}</td>
                  <td className="px-4 py-3">{conn.to_org_name || conn.to_org_id}</td>
                  <td className="px-4 py-3 text-xs">{conn.connection_type}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className="text-xs font-semibold text-red-600"
                      onClick={() => handleDelete(conn.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
